/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormRadioGroup,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import ProductFormFieldLayout from './ProductFormFieldLayout';
import { RadioButtonAndLabel } from './RadioButtonAndLabel';
import { useShortTestHelpers } from '../../../hooks/useShortTestHelpers';
import type { FormFieldProps } from '../../../types';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';
import { triggerValidations } from '../utils';

const ObservationForecast: React.FC<FormFieldProps> = ({
  isDisabled,
  isReadOnly,
}: FormFieldProps) => {
  const { t } = useSigmetAirmetTranslation();
  const { watch, trigger, getValues } = useFormContext();
  const { isRequired: isRequiredField } = useDraftFormHelpers();
  const { isShortTest } = useShortTestHelpers();
  const isRequired = (value: string): string | boolean =>
    isShortTest() || isRequiredField(value);
  const fieldsDisabled = isDisabled || isShortTest();

  return (
    <ProductFormFieldLayout
      sx={{
        marginTop: -1,
        '.MuiButtonBase-root.MuiRadio-root': {
          padding: '6px',
          marginLeft: '3px',
        },
        '.MuiFormHelperText-root.Mui-error': {
          marginBottom: '-16px',
          marginTop: '3px',
        },
      }}
    >
      <ReactHookFormRadioGroup
        disabled={fieldsDisabled}
        isReadOnly={isReadOnly}
        name="isObservationOrForecast"
        onChange={(): void => {
          triggerValidations(['observationOrForecastTime'], getValues, trigger);
        }}
        rules={{
          validate: {
            isRequired,
          },
        }}
      >
        {(!isReadOnly || watch('isObservationOrForecast') === 'OBS') && (
          <RadioButtonAndLabel
            data-testid="isObservationOrForecast-OBS"
            disabled={fieldsDisabled}
            label={t('observation-label')}
            value="OBS"
          />
        )}
        {(!isReadOnly || watch('isObservationOrForecast') === 'FCST') && (
          <RadioButtonAndLabel
            data-testid="isObservationOrForecast-FCST"
            disabled={fieldsDisabled}
            label={t('forecast-label')}
            value="FCST"
          />
        )}
      </ReactHookFormRadioGroup>
    </ProductFormFieldLayout>
  );
};

export default ObservationForecast;
